// TMP: This file needs review of types, but Infographics v3 is also a task.
// Lets do fixing when infographic is the focus, and not when moving parts
// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js';
import classNames from 'classnames';
import tokens from '@gjensidige/builders-tokens/dist/tokens-web.json';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Button, Flex, Text, Title } from '@gjensidige/builders-components';
import { Chart as Props } from '../types';
import { configureChartJs } from './chartConfig';
import {
  colorPalette,
  convertArrayToHtml,
  getDatasetForType,
  getOptionsForType,
  parseDataProp
} from '../utils';
import styles from '../infographics.module.less';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Filler,
  PointElement
);

configureChartJs(ChartJS);

const TextBox = ({
  description,
  buttons,
  fontLarge,
  footnotes,
  numberOfCharts,
  title,
  titleTag
}) => {
  const noText = !title && !description && !footnotes;
  if (noText) return null;

  return (
    <div>
      {!title || (
        <Title
          as={titleTag ?? 'h2'}
          size={fontLarge ? (numberOfCharts === 1 ? 1 : 2) : 4}
        >
          {title}
        </Title>
      )}
      {!description || (
        <Text
          as="div"
          className={styles.htmlArea}
          dangerouslySetInnerHTML={{ __html: description }}
          size={fontLarge ? (numberOfCharts === 1 ? 'lead' : 'large') : 'body'}
        />
      )}
      {!footnotes || (
        <Text
          as="div"
          dangerouslySetInnerHTML={{ __html: footnotes }}
          className={styles.htmlArea}
        />
      )}
      {!buttons || (
        <Flex gap="sm" className={styles.buttons}>
          {buttons?.map(({ linkText, linkTarget }, key: number) => (
            <Button variant="action" href={linkTarget} key={key}>
              {linkText}
            </Button>
          ))}
        </Flex>
      )}
    </div>
  );
};
const ChartBox = ({ chart, layout, type }) => {
  const chartTypes = {
    doughnut: Doughnut,
    bar: Bar,
    barGrouped: Bar,
    barGroupedHorizontal: Bar,
    barStacked: Bar,
    barStackedHorizontal: Bar,
    line: Line,
    lineWithoutShade: Line
  };
  const ChartComponent = chartTypes[type];
  return (
    <div style={layout === 'description' ? { order: 2 } : undefined}>
      <div
        aria-hidden="true"
        className={classNames(styles.chartBox, {
          [styles.maxHeightDougnut]: type === 'doughnut'
        })}
      >
        {chart.data && (
          <ChartComponent data={chart.data} options={chart.options} />
        )}
      </div>
    </div>
  );
};

export const Chart = ({
  backgroundColour,
  buttons,
  data,
  dataSourceUrl,
  description,
  fontLarge = false,
  footnotes,
  layout = 'description',
  numberOfCharts = 1,
  title,
  titleTag,
  type
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [chart, setChart] = useState({ data: null, options: null });
  const [htmlData, setHtmlData] = useState(dataSourceUrl ? undefined : data);

  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []); // Same as canUseDOM

  useEffect(() => {
    if (dataSourceUrl) {
      const getDataFromExtSource = async () => {
        try {
          const res = await fetch(dataSourceUrl);
          const json = await res.json();
          setHtmlData(convertArrayToHtml(json.labels, json.datasets));
        } catch (err) {
          console.log('Error fetching external data');
        }
      };
      getDataFromExtSource();
    }
  }, [dataSourceUrl]);

  const parsedTypes = {
    doughnut: 'doughnut',
    barGrouped: 'bar',
    barGroupedHorizontal: 'bar',
    barStacked: 'bar',
    barStackedHorizontal: 'bar',
    line: 'line',
    lineWithoutShade: 'line'
  };

  const stacked = type === 'barStacked' || type === 'barStackedHorizontal';
  const horizontal =
    type === 'barGroupedHorizontal' || type === 'barStackedHorizontal';

  useEffect(() => {
    if (ref.current) {
      const { labels, datasets, tooltips } = parseDataProp(ref.current);
      const chartColors = colorPalette.slice(0, labels.length);

      setChart({
        data: {
          labels,
          datasets: (type === 'doughnut' ? datasets.slice(0, 1) : datasets).map(
            (row, index) => {
              // Remove shade if more than 1 chart
              const mappedType =
                type === 'line' && numberOfCharts > 1
                  ? 'lineWithoutShade'
                  : type;
              const mappedSet = getDatasetForType(row, index)[
                mappedType as keyof ReturnType<typeof getDatasetForType>
              ];

              return (
                mappedSet || {
                  label: row[0],
                  backgroundColor:
                    (type === 'doughnut' ? datasets.slice(0, 1) : datasets)
                      .length > 1
                      ? chartColors[index]
                      : chartColors,
                  hoverBackgroundColor:
                    (type === 'doughnut' ? datasets.slice(0, 1) : datasets)
                      .length > 1
                      ? chartColors[index]
                      : chartColors,
                  hoverBorderColor: tokens.color.blue700,
                  hoverBorderJoinStyle: 'bevel',
                  data: row.slice(1),
                  cutout: '45%',
                  rotation: 270,
                  borderWidth: 1,
                  borderColor: tokens.color.blue700,
                  hoverOffset: 30
                }
              );
            }
          )
        },
        options: getOptionsForType(
          datasets,
          tooltips,
          5,
          stacked,
          horizontal,
          numberOfCharts
        )[parsedTypes[type as keyof typeof parsedTypes]]
      });
    }
  }, [ref, type, htmlData]);

  return htmlData ? (
    <Flex
      align="center"
      className={classNames(
        styles[backgroundColour as keyof typeof styles],
        styles.chartContainer
      )}
      gap="xl"
      layout={{ xs: 1, lg: numberOfCharts > 1 ? 1 : 2 }}
    >
      {!!hydrated && <ChartBox chart={chart} layout={layout} type={type} />}
      <TextBox
        description={description}
        buttons={buttons}
        fontLarge={fontLarge}
        footnotes={footnotes}
        numberOfCharts={numberOfCharts}
        title={title}
        titleTag={titleTag}
      />
      <div
        className={styles.srOnly}
        dangerouslySetInnerHTML={{ __html: htmlData }}
        ref={ref}
      />
    </Flex>
  ) : null;
};
